import React, { useState, useEffect, useCallback, useRef } from 'react';
import { UserPlus } from 'lucide-react';
import CustomerSearch from './CustomerSearch';
import CustomerCard from './CustomerCard';
import useCustomerActions from '../../hooks/useCustomerActions';
import { useConfirm } from '../../contexts/ConfirmContext';
import { apiFetch } from '../../utils/apiClient';

// 카드형 UI라 한 줄짜리 표보다 세로로 훨씬 길다 - 50개씩 끊으면 한
// 페이지가 너무 길어져서 페이지를 나눈 의미가 옅어지므로 30으로 둔다.
const PAGE_SIZE = 30;

const Customer = ({ storeInfo }) => {
  const confirm = useConfirm();
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [currentPage, setCurrentPage] = useState(1);
  const listTopRef = useRef(null);

  const {
    stampRules,
    fetchStampRules,
    actioningPhone,
    addStamp,
    fetchCoupons,
    fetchCustomer,
    redeemCoupon,
  } = useCustomerActions(storeInfo);

  const fetchCustomers = useCallback(async () => {
    if (!storeInfo?.id) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(
        `/api/customers?store_id=${encodeURIComponent(storeInfo.id)}&sort=${sortBy}`
      );
      const result = await response.json();
      if (result.success) {
        setCustomers(result.data || []);
      } else {
        setError(result.message || '고객 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      setError('네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [storeInfo, sortBy]);

  useEffect(() => {
    fetchCustomers();
    fetchStampRules();
  }, [fetchCustomers, fetchStampRules]);

  // 목록을 통째로 다시 불러오면 정렬 순서상 방금 처리한 고객이 맨 위로 튀어서
  // 연속으로 여러 명을 처리하기 불편해진다. 이미 목록에 있는 고객이면
  // 그 고객 정보만 갱신해서 자리를 유지하고, 목록에 없던(신규 등록) 고객일 때만
  // 전체를 다시 불러온다.
  const refreshAfterAction = async (phone) => {
    const alreadyInList = customers.some((c) => c.phone === phone);
    if (!alreadyInList) {
      await fetchCustomers();
      return;
    }
    try {
      const updated = await fetchCustomer(phone);
      setCustomers((prev) => prev.map((c) => (c.phone === phone ? updated : c)));
    } catch (err) {
      // 단건 갱신 실패 시 조용히 무시(다음 새로고침 때 맞춰짐)
    }
  };

  const handleStampAdd = async (phone) => {
    setError('');
    try {
      await addStamp(phone);
      await refreshAfterAction(phone);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCouponUse = async (phone, couponNumber) => {
    setError('');
    try {
      await redeemCoupon(phone, couponNumber);
      await refreshAfterAction(phone);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddNewCustomer = async (e) => {
    e.preventDefault();
    const digits = newPhone.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 11) {
      setError('올바른 휴대폰 번호를 입력해주세요.');
      return;
    }
    if (!(await confirm(`${newPhone} 고객에게 스탬프를 적립하시겠습니까?`))) {
      return;
    }
    await handleStampAdd(digits);
    setNewPhone('');
  };

  const filteredCustomers = customers.filter(customer =>
    customer.phone.includes(searchTerm) || customer.phone_short.includes(searchTerm)
  );

  // 검색어/정렬이 바뀌면 이전에 보던 페이지 번호가 새 결과 범위를 벗어날
  // 수 있어(예: 5페이지 보다가 검색해서 결과가 2개로 줄어듦) 1페이지로 되돌린다.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / PAGE_SIZE));
  const pagedCustomers = filteredCustomers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // 페이지를 넘기면 새 페이지는 항상 목록 맨 위부터 보여줘야 하는데,
  // 그냥 두면 스크롤 위치가 그대로라 페이지 버튼이 있던 아래쪽에 머물러
  // 있다 - 새 카드들을 보려면 매번 위로 직접 스크롤해야 해서 불편하다.
  useEffect(() => {
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPage]);

  const totalStamps = customers.reduce((sum, customer) => sum + customer.stamps, 0);

  return (
    <div className="space-y-6">
      {/* 전화번호로 적립 - 맨 위로. 신규/기존 고객 구분 없이 번호만 입력하면
          적립되는 동작이라(서버가 upsert) 검색/목록과 무관하게 독립적으로
          쓸 수 있어 맨 앞에 둔다. 매장 설정의 "기본 정보" 카드와 같은
          틀(제목 헤더 + 구분선 + 본문)을 그대로 씀. */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-3 border-b">
          <h3 className="text-lg font-semibold flex items-center">
            <UserPlus className="w-5 h-5 mr-2 text-gray-600" />
            스탬프 적립
          </h3>
        </div>
        <div className="p-3">
          {/* 좁은 화면에서는 input의 flex 기본 min-width 때문에 버튼이
              밀려서 잘려 보이는 문제가 있었다 - 세로로 쌓이게 바꿔서
              아예 좁아질 일이 없게 함. */}
          <form onSubmit={handleAddNewCustomer} className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              placeholder="고객 휴대폰 번호 (예: 01012345678)"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="submit"
              className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors whitespace-nowrap"
            >
              적립
            </button>
          </form>
        </div>
      </div>

      {/* 정렬 */}
      <div className="flex gap-2">
        <button
          onClick={() => setSortBy('recent')}
          className={`px-3 py-1.5 rounded text-sm ${
            sortBy === 'recent' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 border'
          }`}
        >
          최근 방문순
        </button>
        <button
          onClick={() => setSortBy('frequent')}
          className={`px-3 py-1.5 rounded text-sm ${
            sortBy === 'frequent' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 border'
          }`}
        >
          방문 많은 순
        </button>
      </div>

      {/* 전화번호 검색 - 여기 입력하면 바로 아래 고객 목록이 바뀌므로
          목록 바로 위에 붙여둔다. */}
      <CustomerSearch
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        totalCustomers={filteredCustomers.length}
        totalStamps={totalStamps}
        onRefresh={fetchCustomers}
        refreshing={loading}
      />

      {error && (
        <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3">{error}</div>
      )}

      {/* 고객 목록 */}
      <div ref={listTopRef} className="bg-white rounded-lg shadow">
        <div className="p-3">
          <div className="space-y-4">
            {/* 최초 진입(목록이 아직 없음)일 때만 목록 자리를 통째로 로딩
                문구로 바꾼다. 이미 목록이 있는 상태에서 새로고침 버튼을
                눌렀을 때도 매번 이렇게 하면, 목록이 잠깐 사라졌다 다시
                채워지며 페이지 높이가 줄었다 늘었다 해서 화면이 들썩인다
                (실제로 겪음) - 새로고침 중엔 기존 목록을 그대로 두고
                버튼 아이콘 회전으로만 진행 중임을 알린다. */}
            {loading && customers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">불러오는 중...</div>
            ) : filteredCustomers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                검색 결과가 없습니다.
              </div>
            ) : (
              pagedCustomers.map(customer => (
                <CustomerCard
                  key={customer.phone}
                  customer={customer}
                  stampRules={stampRules}
                  isBusy={actioningPhone === customer.phone}
                  onStampAdd={handleStampAdd}
                  onFetchCoupons={fetchCoupons}
                  onCouponUse={handleCouponUse}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* 페이지네이션 - 검색/정렬로 결과가 한 페이지 분량 이하면 안 보여줌 */}
      {!(loading && customers.length === 0) && totalPages > 1 && (
        <div className="flex justify-center flex-wrap gap-1 pt-1">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1.5 rounded text-sm ${
                currentPage === page ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 border'
              }`}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Customer;
